import React, { useMemo, useState } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Card, Select, Typography } from 'antd';

import ColoredFormattedNumber from 'components/ColoredFormattedNumber';
import { saveAs } from 'file-saver';
import { formatDate } from 'helpers/date';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { tap } from 'rxjs/operators';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import ValueCard from 'v2/components/Shared/ValueCard';
import type { ICardProps } from 'v2/components/Shared/ValueCard';
import { SuspendChartTypes } from 'v2/interfaces/reports/suspend';
import { suspendService } from 'v2/services/reports/suspend';

import { Icon } from '@eduzz/ui-antd-theme';

interface ICustomLabel {
  x: number;
  y: number;
  width: number;
  height: number;
  value: number;
}

interface IChartData {
  name: string;
  value: number;
}

export function ChartTab() {
  const { message } = App.useApp();
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [typeChart, setTypeChart] = useState<string>(SuspendChartTypes.PERCENTAGE);
  const [chartData] = useObservable(() => suspendService.getChart(), []);
  const [isLoading] = useObservable(() => suspendService.isLoading(), []);
  const [handleExport] = useCallbackObservable(() => {
    setLoadingExport(true);
    return suspendService.export().pipe(
      tap(
        data => {
          try {
            const currentDate = formatDate(new Date(), 'dd-MM-yyyy');
            saveAs(data, `Contratos_MotivosSuspensao-${currentDate}.xlsx`);
          } finally {
            setLoadingExport(false);
          }
        },
        () => {
          setLoadingExport(false);
          message.error('Algo de errado aconteceu, tente exportar novamente.');
        }
      )
    );
  }, [message]);

  const chart = chartData?.chart || [];
  const subscriptionsAmount = chartData?.subscriptions_amount || 0;
  const subscriptionsTotal = chartData?.subscriptions_total || 0;

  const incomeCards: ICardProps[] = useMemo(
    () => [
      {
        title: 'Total de suspensões',
        total: !!isLoading ? '-' : subscriptionsTotal,
        helpText: 'Quantia total de contratos suspensos'
      },
      {
        title: 'Valor total',
        total: !!isLoading ? '-' : <ColoredFormattedNumber value={subscriptionsAmount} format='BRL' />,
        helpText: 'Valor total de contratos suspensos'
      }
    ],
    [isLoading, subscriptionsAmount, subscriptionsTotal]
  );

  const handleChangeChart = (value: string) => {
    return setTypeChart(value);
  };

  const data: Array<IChartData> = chart.map(item => ({
    name: item.reason,
    value: typeChart === SuspendChartTypes.PERCENTAGE ? item.percentage : item.amount
  }));

  const maxDataValue = Math.max(...data.map(i => i.value));
  const domainMax = Math.round(maxDataValue + maxDataValue * 0.5);

  const CustomizedLabel = ({ x, y, width, height, value }: ICustomLabel) => {
    const formatValue = (valueToFormat: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valueToFormat);
    const formattedValue = typeChart === SuspendChartTypes.PERCENTAGE ? `${value}%` : formatValue(value);
    return (
      <text
        x={x + width + 10}
        y={y + height / 2 + 10}
        fill='var(--uizz-antd-color-text-secondary)'
        textAnchor='right'
        dy={-6}
      >
        {formattedValue}
      </text>
    );
  };

  return (
    <>
      <div className='mb-2.5 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4'>
        {incomeCards.map(card => (
          <div key={card.title}>
            <ValueCard {...card} />
          </div>
        ))}
      </div>

      <Card variant='borderless'>
        {data.length === 0 ? (
          <Typography.Paragraph className='mb-0 text-center text-base'>
            Nenhum registro encontrado.
          </Typography.Paragraph>
        ) : (
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-12'>
            <div className='flex items-center md:col-span-3'>
              <Select
                className='w-full'
                value={typeChart}
                onChange={handleChangeChart}
                options={[
                  { value: SuspendChartTypes.PERCENTAGE, label: 'Motivos por percentual (%)' },
                  { value: SuspendChartTypes.CURRENCY, label: 'Motivos por valor' }
                ]}
              />
            </div>

            <PermissionHide action={'myeduzz:reports:exportar'}>
              <div className='flex items-center justify-end text-(--uizz-antd-color-text) md:col-span-9'>
                <Button
                  color='default'
                  variant='filled'
                  disabled={!!isLoading || !!loadingExport}
                  onClick={handleExport}
                  loading={!!isLoading || !!loadingExport}
                  icon={<Icon.Download size={16} />}
                >
                  {isLoading || loadingExport ? 'Exportando...' : 'Exportar'}
                </Button>
              </div>
            </PermissionHide>

            <div className='h-100 overflow-x-auto overflow-y-hidden md:col-span-12'>
              <ResponsiveContainer width='100%' height='100%' minWidth={800}>
                <BarChart layout='vertical' data={data} barCategoryGap='25%' margin={{ top: 30 }}>
                  <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                  <XAxis type='number' axisLine={false} domain={[0, domainMax]} hide />
                  <YAxis type='category' dataKey='name' width={450} />
                  <Bar
                    dataKey='value'
                    fill='var(--uizz-antd-color-primary)'
                    isAnimationActive={false}
                    label={CustomizedLabel}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
